import React, { useState, useMemo } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DomainHit } from '@/lib/trustHub/v1/types';
import { ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface SimpleResultsTableProps {
  domains: DomainHit[];
  totalQueries: number;
}

export const SimpleResultsTable: React.FC<SimpleResultsTableProps> = ({
  domains,
  totalQueries,
}) => {
  const [expandedDomains, setExpandedDomains] = useState<Set<string>>(new Set());
  const [expandedQueries, setExpandedQueries] = useState<Set<string>>(new Set());

  // Calculate total citations across all domains
  const totalCitations = domains.reduce((sum, hit) => {
    const openaiCount = hit.providerBreakdown.openai?.usedCount || 0;
    const claudeCount = hit.providerBreakdown.claude?.usedCount || 0;
    return sum + openaiCount + claudeCount;
  }, 0);

  const toggleDomain = (domain: string) => {
    const newExpanded = new Set(expandedDomains);
    if (newExpanded.has(domain)) {
      newExpanded.delete(domain);
      // Also collapse all queries for this domain
      const queriesWithDomain = new Set(expandedQueries);
      expandedQueries.forEach(key => {
        if (key.startsWith(`${domain}|`)) {
          queriesWithDomain.delete(key);
        }
      });
      setExpandedQueries(queriesWithDomain);
    } else {
      newExpanded.add(domain);
    }
    setExpandedDomains(newExpanded);
  };

  const toggleQuery = (domain: string, query: string) => {
    const key = `${domain}|${query}`;
    const newExpanded = new Set(expandedQueries);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedQueries(newExpanded);
  };

  const getQueriesForDomain = (hit: DomainHit) => {
    return Array.from(hit.usedBy);
  };

  const getUrlsForDomainQuery = (hit: DomainHit, query: string) => {
    // Prefer query-specific URLs if available
    const queryUrls = hit.queryBreakdown?.[query]?.usedUrls || [];

    const urlMap = new Map<string, { url: string; providers: Set<'openai' | 'claude'> }>();

    const addUrlWithProviders = (url: string) => {
      if (!urlMap.has(url)) {
        urlMap.set(url, { url, providers: new Set() });
      }
      const entry = urlMap.get(url)!;
      // Prefer provider mapping scoped to this query
      const qb = hit.queryBreakdown?.[query];
      const perQueryProviders = qb?.providerByUrl?.[url];
      if (perQueryProviders && perQueryProviders.size > 0) {
        perQueryProviders.forEach((p) => entry.providers.add(p));
      } else {
        // Fallback: mark providers based on global provider breakdown
        if (hit.providerBreakdown.openai?.usedUrls?.includes(url)) {
          entry.providers.add('openai');
        }
        if (hit.providerBreakdown.claude?.usedUrls?.includes(url)) {
          entry.providers.add('claude');
        }
      }
    };

    if (queryUrls.length > 0) {
      queryUrls.forEach(addUrlWithProviders);
    } else {
      // Fallback: combine all provider URLs (legacy behavior)
      hit.providerBreakdown.openai?.usedUrls?.forEach(addUrlWithProviders);
      hit.providerBreakdown.claude?.usedUrls?.forEach(addUrlWithProviders);
    }

    return Array.from(urlMap.values());
  };

  // Sort domains by total count (descending)
  const sortedDomains = useMemo(() => {
    return [...domains].sort((a, b) => {
      const aTotal = (a.providerBreakdown.openai?.usedCount || 0) + 
                     (a.providerBreakdown.claude?.usedCount || 0);
      const bTotal = (b.providerBreakdown.openai?.usedCount || 0) + 
                     (b.providerBreakdown.claude?.usedCount || 0);
      return bTotal - aTotal;
    });
  }, [domains]);

  return (
    <div className="overflow-x-auto">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{sortedDomains.length}</span> unique domains found
        </div>
        <div className="text-sm text-muted-foreground">
          Total citations: <span className="font-semibold text-foreground">{totalCitations}</span>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Rank</TableHead>
            <TableHead>Domain</TableHead>
            <TableHead className="w-48">Provider Count</TableHead>
            <TableHead className="w-24 text-right">Total</TableHead>
            <TableHead className="w-24 text-right">Coverage %</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedDomains.map((hit, index) => {
            const openaiCount = hit.providerBreakdown.openai?.usedCount || 0;
            const claudeCount = hit.providerBreakdown.claude?.usedCount || 0;
            
            const domainTotal = openaiCount + claudeCount;
            const coveragePercent = totalCitations > 0 ? ((domainTotal / totalCitations) * 100).toFixed(1) : '0.0';
            const maxCount = Math.max(openaiCount, claudeCount, 1);
            const isExpanded = expandedDomains.has(hit.domain);
            const queries = getQueriesForDomain(hit);

            return (
              <React.Fragment key={hit.domain}>
                <TableRow 
                  className="cursor-pointer hover:bg-muted/70"
                  onClick={() => toggleDomain(hit.domain)}
                >
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      {index + 1}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <img
                        src={hit.favicon}
                        alt=""
                        className="w-4 h-4"
                        onError={(e) => {
                          e.currentTarget.src = `https://www.google.com/s2/favicons?domain=${hit.domain}&sz=32`;
                        }}
                      />
                      <span className="font-mono text-sm">{hit.domain}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium w-16">OpenAI</span>
                        <div className="flex-1 bg-secondary rounded-full h-4 overflow-hidden">
                          <div
                            className="bg-blue-500 h-full transition-all"
                            style={{ width: `${(openaiCount / maxCount) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs font-mono w-8 text-right">{openaiCount}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium w-16">Claude</span>
                        <div className="flex-1 bg-secondary rounded-full h-4 overflow-hidden">
                          <div
                            className="bg-purple-500 h-full transition-all"
                            style={{ width: `${(claudeCount / maxCount) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs font-mono w-8 text-right">{claudeCount}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono font-semibold text-lg">
                    {domainTotal}
                  </TableCell>
                  <TableCell className="text-right font-mono font-medium">
                    {coveragePercent}%
                  </TableCell>
                </TableRow>

                {/* Expanded: Show queries */}
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={4} className="bg-muted/30 p-0">
                      <div className="p-4 space-y-2">
                        <div className="text-sm font-medium text-muted-foreground mb-3">
                          Queries that cited this domain ({queries.length}):
                        </div>
                        {queries.map((query) => {
                          const queryKey = `${hit.domain}|${query}`;
                          const isQueryExpanded = expandedQueries.has(queryKey);
                          const urls = getUrlsForDomainQuery(hit, query);

                          return (
                            <div key={query} className="border rounded-lg">
                              <div 
                                className="flex items-start gap-2 p-3 cursor-pointer hover:bg-muted/50 rounded-lg"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleQuery(hit.domain, query);
                                }}
                              >
                                <div className="flex-shrink-0 mt-0.5">
                                  {isQueryExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm">{query}</div>
                                </div>
                              </div>

                              {/* Expanded: Show URLs */}
                              {isQueryExpanded && urls.length > 0 && (
                                <div className="px-3 pb-3 pt-0 space-y-2 border-t bg-background/50">
                                  <div className="text-xs font-medium text-muted-foreground mt-3 mb-2">
                                    URLs from this domain ({urls.length}):
                                  </div>
                                  {urls.map((urlData, idx) => {
                                    const providers = Array.from(urlData.providers);
                                    const isBoth = providers.length === 2;
                                    
                                    return (
                                      <div key={idx} className="flex items-start gap-2 text-xs p-2 rounded bg-muted/30 hover:bg-muted/50">
                                        <ExternalLink className="h-3 w-3 flex-shrink-0 mt-0.5" />
                                        <a 
                                          href={urlData.url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="flex-1 break-all hover:underline text-primary"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {urlData.url}
                                        </a>
                                        <div className="flex gap-1 flex-shrink-0">
                                          {isBoth ? (
                                            <Badge variant="secondary" className="text-xs px-2 py-0">
                                              Both
                                            </Badge>
                                          ) : providers.includes('openai') ? (
                                            <Badge variant="secondary" className="text-xs px-2 py-0 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                              OpenAI
                                            </Badge>
                                          ) : (
                                            <Badge variant="secondary" className="text-xs px-2 py-0 bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                                              Claude
                                            </Badge>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
